import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useCash } from '../../context/CashContext';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CalendarDays, LogOut, RefreshCw, Lock, ArrowRight, Banknote } from 'lucide-react';
import { Logo } from '../common/Logo';

const todayLabel = (locale) => {
  const s = new Date().toLocaleDateString(locale || 'it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const Screen = ({ children }) => (
  <div className="min-h-dvh flex items-center justify-center bg-background p-6" data-testid="cash-gate">
    <Card className="w-full max-w-md p-8">{children}</Card>
  </div>
);

const Brand = ({ subtitle }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 mb-6">
      <Logo className="h-11 w-11" />
      <div>
        <div className="font-display font-bold leading-tight">{t('common.appName')}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
    </div>
  );
};

const OpenDayScreen = () => {
  const { t, i18n } = useTranslation();
  const { openDay } = useCash();
  const { logout } = useAuth();
  const [step, setStep] = useState(1);
  const [cash, setCash] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await openDay(cash);
      toast.success(t('cash.dayOpenedToast'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('cash.openError'));
      setBusy(false);
    }
  };

  return (
    <Screen>
      <Brand subtitle={t('cash.openDayTitle')} />
      {step === 1 ? (
        <div data-testid="open-day-step1">
          <div className="rounded-2xl bg-primary/10 text-primary h-16 w-16 flex items-center justify-center mb-5"><CalendarDays className="h-8 w-8" /></div>
          <h1 className="font-display text-2xl font-black tracking-tight">{t('cash.startDay')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('cash.startDayDesc')}</p>
          <div className="mt-5 rounded-xl border border-border p-4 flex items-center gap-3">
            <CalendarDays className="h-5 w-5 text-muted-foreground" />
            <div>
              <div className="text-xs text-muted-foreground">{t('cash.today')}</div>
              <div className="font-semibold" data-testid="open-day-date">{todayLabel(i18n.language)}</div>
            </div>
          </div>
          <Button className="w-full h-12 rounded-xl mt-6 text-base" data-testid="open-day-confirm-start" onClick={() => setStep(2)}>
            {t('cash.startDayBtn')} <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
          <button onClick={logout} className="w-full text-center text-xs text-muted-foreground hover:text-primary mt-4 transition-colors" data-testid="open-day-logout">{t('cash.logout')}</button>
        </div>
      ) : (
        <div data-testid="open-day-step2">
          <div className="rounded-2xl bg-primary/10 text-primary h-16 w-16 flex items-center justify-center mb-5"><Banknote className="h-8 w-8" /></div>
          <h1 className="font-display text-2xl font-black tracking-tight">{t('cash.openingFundTitle')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('cash.openingFundDesc')}</p>
          <div className="mt-5">
            <Label>{t('cash.openingCashLabel')}</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">€</span>
              <Input type="number" min="0" step="0.01" value={cash} onChange={(e) => setCash(e.target.value)} placeholder="0.00" className="h-12 pl-8 text-lg" data-testid="open-day-cash-input" />
            </div>
          </div>
          <Button className="w-full h-12 rounded-xl mt-6 text-base" disabled={busy} onClick={submit} data-testid="open-day-submit">
            {busy ? t('cash.opening') : t('cash.confirmOpen')}
          </Button>
          <button onClick={() => setStep(1)} className="w-full text-center text-xs text-muted-foreground hover:text-primary mt-4 transition-colors">{t('cash.back')}</button>
        </div>
      )}
    </Screen>
  );
};

const DayClosedBlock = () => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { refresh } = useCash();
  const [checking, setChecking] = useState(false);
  const retry = async () => { setChecking(true); await refresh(); setTimeout(() => setChecking(false), 600); };
  return (
    <Screen>
      <Brand subtitle={t('cash.accessBlocked')} />
      <div className="rounded-2xl bg-pos-bill/10 text-pos-bill h-16 w-16 flex items-center justify-center mb-5"><Lock className="h-8 w-8" /></div>
      <h1 className="font-display text-2xl font-black tracking-tight">{t('cash.dayNotOpen')}</h1>
      <p className="text-muted-foreground text-sm mt-2" data-testid="day-closed-message">
        {t('cash.dayClosedMsg')}
      </p>
      <Button className="w-full h-12 rounded-xl mt-6" variant="outline" onClick={retry} data-testid="day-closed-retry">
        <RefreshCw className={`h-4 w-4 mr-2 ${checking ? 'animate-spin' : ''}`} /> {t('cash.retry')}
      </Button>
      <button onClick={logout} className="w-full text-center text-xs text-muted-foreground hover:text-primary mt-4 transition-colors flex items-center justify-center gap-1" data-testid="day-closed-logout">
        <LogOut className="h-3.5 w-3.5" /> {t('cash.logout')}
      </button>
    </Screen>
  );
};

const CashSplash = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-dvh flex items-center justify-center bg-background">
      <div className="flex items-center gap-3 text-muted-foreground"><RefreshCw className="h-5 w-5 animate-spin" /> {t('common.loading')}</div>
    </div>
  );
};

/**
 * Gate that blocks the operational app until the business day is open.
 * - Admin/Manager: shown the open-day flow (confirm date -> enter opening cash).
 * - Other roles: shown a blocking message to start the day from the TPV.
 */
export const CashGate = ({ children }) => {
  const { user } = useAuth();
  const { session, loading } = useCash();
  const isManager = ['admin', 'manager'].includes(user?.role);
  if (loading) return <CashSplash />;
  if (session) return children;
  return isManager ? <OpenDayScreen /> : <DayClosedBlock />;
};
